import { useTenantStore } from "@/stores/tenantStore";
import { Button } from "@repo/ui/components/Base/Button";
import { RichTextEditor } from "@repo/ui/components/Base/RichTextEditor";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { useRef } from "react";
import Quill from "quill";
import { Formik } from "formik";
import type { Delta, QuillOptions } from "quill/core";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { meetingRepo } from "@/repositories/meetingRepo";

export const meetingMinutesEditorOptions: QuillOptions = {
  formats: [
    "background",
    "bold",
    "color",
    "font",
    "italic",
    "link",
    "size",
    "strike",
    "script",
    "underline",
    "blockquote",
    "header",
    "indent",
    "list",
    "align",
    "direction",
    "divider",
  ],
  modules: {
    toolbar: [
      [{ font: [] }, { size: [] }, { header: [] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [
        { indent: "-1" },
        { indent: "+1" },
        { list: "ordered" },
        { list: "bullet" },
      ],
      [
        { align: "" },
        { align: "center" },
        { align: "right" },
        { align: "justify" },
      ],
      ["blockquote", "link", { divider: "xyz" }],
    ],
  },
};

export interface MeetingMinutesEditorFormProps {
  meetingId?: string;
  handleCancel?: () => void;
  handleSubmit?: () => void;
  minutes: null | Delta | string;
  withinDialog?: boolean;
}

export const MeetingMinutesEditorForm = ({
  meetingId,
  handleCancel,
  handleSubmit,
  minutes = null,
  withinDialog,
}: MeetingMinutesEditorFormProps) => {
  const quillRef = useRef<Quill>();
  const tenant = useTenantStore((s) => s.tenant);
  const tenantId = tenant?.id ?? "";

  if (!minutes) {
    return (
      <div className="flex flex-col flex-grow justify-center items-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Formik
      initialValues={{}}
      onSubmit={async () => {
        if (!tenantId || !meetingId || !quillRef.current) return;

        quillRef.current.disable();

        const minutesData = {
          json: JSON.stringify(quillRef.current.getContents()),
        };

        console.log(minutesData);

        await meetingRepo.update(tenantId, meetingId, { minutesData });

        handleSubmit?.();
      }}
    >
      {({ submitForm, isSubmitting }) => {
        const footer = (
          <>
            <Button
              disabled={isSubmitting}
              variant="destructive"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button disabled={isSubmitting} onClick={submitForm}>
              Save
            </Button>
          </>
        );

        const form = (
          <div className="flex flex-col flex-grow gap-y-4">
            <RichTextEditor
              className="mt-5 h-0 flex-grow flex flex-col"
              containerStyles="h-0 flex-grow"
              options={meetingMinutesEditorOptions}
              contents={minutes}
              ref={quillRef}
            />
            {!withinDialog && (
              <div className="flex flex-row justify-between">{footer}</div>
            )}
          </div>
        );

        return withinDialog ? (
          <>
            <DialogBody className="h-[70vh] flex flex-col">{form}</DialogBody>
            <DialogFooter className="justify-between">{footer}</DialogFooter>
          </>
        ) : (
          form
        );
      }}
    </Formik>
  );
};
